import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useCombobox } from 'downshift-v9';
import {
  itemToString as toDisplay,
  buildAnnouncementMessage,
  isNoResultsAnnouncement,
} from './helpers';
import InputWithClear from './InputWithClear';
import AutosuggestOption from './AutosuggestOption';
import AutosuggestOptions from './AutosuggestOptions';
import './sass/autosuggest.scss';
import { AutosuggestProps } from './types';
import { srClearOnBlur, srKeepOnBlur } from './StateReducer';
import { MIN_SEARCH_CHARS, SR_ANNOUNCE_DELAY_MS } from './constants';

function Autosuggest({
  // downshift props
  handleOnSelect,
  highlightedIndex: highlightedIndexProp,
  inputValue,
  itemToString = toDisplay,
  onHighlightedIndexChange,
  onInputValueChange,
  selectedItem = null,
  // input props
  onClearClick,
  clearButtonLabel = 'Clear input',
  hintText = null,
  inputContainerClassName = 'input-container', // allows to work with fixed width from facility-locator
  inputError,
  inputId = 'autosuggest-input',
  inputRef,
  label,
  labelSibling = null,
  showDownCaret = true,
  // props from Downshift to pass to the input
  downshiftInputProps,
  // options for the autosuggest to show
  options,
  // showError - use the usa-input-error class to show the error
  showError = false,
  // When falsy, input clears on blur/escape. Pass true to retain input,
  // or an object like { resetHighlightOnClear: true } for additional behavior.
  // In controlled-highlight mode (highlightedIndex + onHighlightedIndexChange),
  // the parent must apply highlightedIndex changes from the callback for the
  // reset-on-clear behavior to be reflected.
  keepDataOnBlur = false,
  isItemDisabled = (item, _index) => {
    return !!item?.disabled; // can choose a different method to say which items are disabled
  },
  stateReducer = srClearOnBlur,
  isLoading = false,
  loadingMessage = '',
  AutosuggestOptionComponent = AutosuggestOption,
  showOptionsRestriction = false,
}) {
  const comboboxArgs = {
    items: options,
    itemToString,
    inputId,
    onSelectedItemChange: handleOnSelect,
    onInputValueChange,
    selectedItem: null,
    inputValue,
    isItemDisabled,
    stateReducer: keepDataOnBlur ? srKeepOnBlur(keepDataOnBlur) : stateReducer,
  };

  if (highlightedIndexProp !== undefined) {
    comboboxArgs.highlightedIndex = highlightedIndexProp;
    comboboxArgs.onHighlightedIndexChange = onHighlightedIndexChange;
  }

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useCombobox(comboboxArgs);
  const inputClearClick = () => {
    onClearClick();
    selectItem(null);
  };

  let shouldBeShown = isOpen;

  if (showOptionsRestriction) {
    const hasMinimumChars =
      !!inputValue && inputValue.length >= MIN_SEARCH_CHARS;
    shouldBeShown = isOpen && hasMinimumChars;
  }

  // Message + politeness are kept in a single state object so the timeout
  // callback performs one atomic update. In React 17 setState calls inside
  // setTimeout aren't batched, so separate updates could briefly render a
  // message in the wrong (polite vs assertive) live region and announce it
  // incorrectly (a11y #142577).
  const [status, setStatus] = useState({ message: '', assertive: false });
  const statusTimerRef = useRef(null);

  useEffect(() => {
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
    }

    if (!shouldBeShown || !inputValue) {
      setStatus({ message: '', assertive: false });
      return undefined;
    }

    const message = buildAnnouncementMessage(
      options,
      itemToString,
      inputValue,
      { isLoading, loadingMessage },
    );
    const assertive = isNoResultsAnnouncement(options, { isLoading });
    statusTimerRef.current = setTimeout(() => {
      setStatus({ message, assertive });
    }, SR_ANNOUNCE_DELAY_MS);

    return () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
      }
    };
  }, [
    shouldBeShown,
    options,
    inputValue,
    itemToString,
    isLoading,
    loadingMessage,
  ]);

  // Clean up status timer on unmount
  useEffect(() => {
    return () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
      }
    };
  }, []);

  const { id } = getMenuProps();

  return (
    <div
      id={`${inputId}-autosuggest-container`}
      className={classNames('autosuggest-container', 'vads-u-width--full', {
        'usa-input-error': showError,
      })}
      data-testid="autosuggest-container"
    >
      <div className={`${inputId}-autosuggest-label-container fl-sm-desktop`}>
        <label className={`${inputId}-label`} {...getLabelProps()}>
          {label}
          {hintText && <span className="usa-hint">{hintText}</span>}
        </label>
        {labelSibling}
      </div>
      {inputError}
      <div className="autosuggest-input-container">
        <InputWithClear
          dropdownIsOpen={shouldBeShown}
          getInputProps={getInputProps}
          getToggleButtonProps={getToggleButtonProps}
          className={inputContainerClassName}
          inputId={inputId}
          inputRef={inputRef}
          isOpen={shouldBeShown}
          showDownCaret={showDownCaret}
          showClearButton={!!inputValue}
          onClearClick={inputClearClick}
          clearButtonLabel={clearButtonLabel}
          downshiftInputProps={downshiftInputProps}
          dropdownId={id}
        >
          <AutosuggestOptions
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedItem={selectedItem}
            options={options}
            inputValue={inputValue}
            isShown={shouldBeShown}
            itemToString={itemToString}
            getMenuProps={getMenuProps}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
            AutosuggestOptionComponent={AutosuggestOptionComponent}
          />
        </InputWithClear>
        {/*
          Two static live regions so the announcement politeness can change
          without toggling aria-live/role on a single node (unreliable across
          screen readers). No-results states use the assertive region so they
          interrupt the combobox's queued semantic announcements (a11y #142577).
        */}
        {/* role="status" implies aria-live="polite"; role="alert" implies
            aria-live="assertive", so the politeness is set by role alone. */}
        <div className="usa-combo-box__status usa-sr-only" role="status">
          {status.assertive ? '' : status.message}
        </div>
        <div className="usa-combo-box__status usa-sr-only" role="alert">
          {status.assertive ? status.message : ''}
        </div>
      </div>
    </div>
  );
}

Autosuggest.propTypes = AutosuggestProps;

export default Autosuggest;
